import { useAppSelector } from '@app/redux/hooks';
import { getCategories, verifyBusinessEmail } from '@app/services/business';
import { newBusinessValidation } from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export const useNewBusinessPage = () => {
  const businessState = useAppSelector((state) => state.business);
  const authState = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [businessEmailError, setBusinessEmailError] = useState<
    string | undefined
  >();
  const newBusinessForm = useForm({
    resolver: newBusinessValidation,
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      address: '',
      category: '',
    },
  });
  const { data: businessCategories = [], isLoading } = useQuery({
    queryKey: ['businessCategories'],
    queryFn: () => getCategories(),
  });

  const {
    mutateAsync: validateBusinessEmail,
    isPending: isValidatingBusinessEmail,
  } = useMutation({
    mutationKey: ['verify-business-email-new-business'],
    mutationFn: (email: string) => verifyBusinessEmail(email),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBusinessEmailValidation = async (
    email: string
  ): Promise<boolean> => {
    setBusinessEmailError(undefined);

    try {
      await validateBusinessEmail(email.trim().toLowerCase());
      return true;
    } catch (err: any) {

        setBusinessEmailError('Business email already exists');
        return false;
    }
  };

  const clearBusinessEmailError = () => setBusinessEmailError(undefined);


  const handleSubmit = async (values: FieldValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setBusinessEmailError(undefined);

    try {
      const ok = await handleBusinessEmailValidation(values.email);
      if (!ok) return;

      const businessData = {
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        address: values.address,
        category: values.category,
        userId: authState.user?.id as string,
      };

      localStorage.setItem('pendingBusinessData', JSON.stringify(businessData));
      navigate('/welcome-existing');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    businessEmailError,
    isValidatingBusinessEmail,
    clearBusinessEmailError,
    newBusinessForm,
    handleSubmit,
    businessState,
    isLoading,
    businessCategories,
  };
};
